'use client';
import React, { useState } from 'react';
import { Building2, Calculator, CreditCard, AlertTriangle, ChevronDown, ChevronUp, Landmark } from 'lucide-react';

const LRA_RATES = [
  { category: 'Developed Land', items: [
    { label: 'Commercial', rate: '1.5%' },
    { label: 'Industrial', rate: '1.5%' },
    { label: 'Residential', rate: '0.25%' },
    { label: 'Farm Land (Urban)', rate: '0.33%' },
    { label: 'Farm Land (Outside Urban)', rate: '0.25%' },
    { label: 'Public Land (Commercial)', rate: '1.0%' },
    { label: 'Public Land (Residential)', rate: '0.14%' },
  ]},
  { category: 'Undeveloped Land', items: [
    { label: 'City / Town Lots', rate: '3.5%' },
    { label: 'Farmland', rate: '4.0%' },
    { label: 'Vacant Land (1+ Acre)', rate: '4.5%' },
    { label: 'Outside City / Town', rate: 'LRD $5/acre' },
  ]},
];

const PAYMENT_CHANNELS = [
  { icon: '📱', label: 'MTN Mobile Money', detail: 'Dial *156#', color: '#fbbf24' },
  { icon: '📱', label: 'Orange Money', detail: 'Dial *144#', color: '#fb923c' },
  { icon: '🏦', label: 'Commercial Bank', detail: 'Any partner bank', color: '#60a5fa' },
  { icon: '🏛️', label: 'LRA Office', detail: 'Cashier payment', color: '#4ade80' },
  { icon: '💻', label: 'E-Filing Portal', detail: 'revenue.lra.gov.lr', color: '#a78bfa' },
];

interface TaxCalcResult {
  propertyType: string;
  rate: string;
  marketValue: number;
  annualTax: number;
}

export default function LRARealPropertyTaxInfo() {
  const [showRates, setShowRates] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [calcValue, setCalcValue] = useState('');
  const [calcType, setCalcType] = useState('Residential');
  const [calcResult, setCalcResult] = useState<TaxCalcResult | null>(null);

  const rateMap: Record<string, number> = {
    'Commercial': 0.015,
    'Industrial': 0.015,
    'Residential': 0.0025,
    'Farm Land (Urban)': 0.0033,
    'Farm Land (Outside Urban)': 0.0025,
    'Public Land (Commercial)': 0.01,
    'Public Land (Residential)': 0.0014,
    'City / Town Lots': 0.035,
    'Farmland': 0.04,
    'Vacant Land (1+ Acre)': 0.045,
  };

  const rateDisplayMap: Record<string, string> = {
    'Commercial': '1.5%',
    'Industrial': '1.5%',
    'Residential': '0.25%',
    'Farm Land (Urban)': '0.33%',
    'Farm Land (Outside Urban)': '0.25%',
    'Public Land (Commercial)': '1.0%',
    'Public Land (Residential)': '0.14%',
    'City / Town Lots': '3.5%',
    'Farmland': '4.0%',
    'Vacant Land (1+ Acre)': '4.5%',
  };

  const handleCalculate = () => {
    const val = parseFloat(calcValue.replace(/,/g, ''));
    if (!val || isNaN(val)) return;
    const rate = rateMap[calcType] ?? 0.0025;
    setCalcResult({
      propertyType: calcType,
      rate: rateDisplayMap[calcType] ?? 'LRD $5/acre',
      marketValue: val,
      annualTax: val * rate,
    });
  };

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div
        className="rounded-xl p-5"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
            <Landmark size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>LRA Real Property Tax</h3>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Official rates · revenue.lra.gov.lr</p>
          </div>
          <span className="ml-auto text-xs px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
            Due: July 1st
          </span>
        </div>

        {/* Key info row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Your Property Type', value: 'Residential', sub: 'Developed Land' },
            { label: 'Applicable Rate', value: '0.25%', sub: 'Of declared market value' },
            { label: 'Tax Due FY2026', value: '$1,250', sub: 'Annual obligation' },
          ].map(item => (
            <div key={item.label} className="rounded-lg p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</p>
              <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{item.value}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item.sub}</p>
            </div>
          ))}
        </div>

        {/* Formula */}
        <div
          className="rounded-lg px-4 py-3 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}
        >
          <Calculator size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
          <div className="text-xs" style={{ color: 'var(--dark-text)' }}>
            <span className="font-semibold" style={{ color: 'var(--accent)' }}>LRA Formula: </span>
            Annual Tax = Declared Market Value × Applicable Rate
          </div>
        </div>
      </div>

      {/* Tax Calculator */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <button
          className="w-full flex items-center justify-between px-5 py-4"
          onClick={() => setShowCalc(c => !c)}
        >
          <div className="flex items-center gap-2">
            <Calculator size={16} style={{ color: 'var(--accent)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Tax Calculator</span>
          </div>
          {showCalc ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
        </button>
        {showCalc && (
          <div className="px-5 pb-5 space-y-3">
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>Property Type</label>
              <select
                value={calcType}
                onChange={e => { setCalcType(e.target.value); setCalcResult(null); }}
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
              >
                {LRA_RATES.map(cat => (
                  <optgroup key={cat.category} label={cat.category}>
                    {cat.items.filter(i => i.rate !== 'LRD $5/acre').map(i => (
                      <option key={i.label} value={i.label}>{i.label} ({i.rate})</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: 'var(--dark-muted)' }}>Declared Market Value (USD)</label>
              <input
                type="text"
                value={calcValue}
                onChange={e => { setCalcValue(e.target.value); setCalcResult(null); }}
                placeholder="e.g. 500,000"
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
              />
            </div>
            <button
              onClick={handleCalculate}
              className="w-full py-2 rounded-lg text-sm font-semibold transition-all duration-150 hover:opacity-90"
              style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
            >
              Calculate Tax
            </button>
            {calcResult && (
              <div className="rounded-lg p-4 space-y-2" style={{ backgroundColor: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.25)' }}>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--dark-muted)' }}>Property Type</span>
                  <span className="font-semibold" style={{ color: 'var(--dark-text)' }}>{calcResult.propertyType}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--dark-muted)' }}>Rate Applied</span>
                  <span className="font-semibold" style={{ color: 'var(--accent)' }}>{calcResult.rate}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span style={{ color: 'var(--dark-muted)' }}>Market Value</span>
                  <span className="font-semibold" style={{ color: 'var(--dark-text)' }}>${calcResult.marketValue.toLocaleString()}</span>
                </div>
                <div className="h-px" style={{ backgroundColor: 'var(--dark-border)' }} />
                <div className="flex justify-between">
                  <span className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>Annual Tax Due</span>
                  <span className="text-lg font-bold" style={{ color: 'var(--accent)' }}>${calcResult.annualTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Official Rate Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <button
          className="w-full flex items-center justify-between px-5 py-4"
          onClick={() => setShowRates(r => !r)}
        >
          <div className="flex items-center gap-2">
            <Building2 size={16} style={{ color: 'var(--accent)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Official LRA Rate Table</span>
          </div>
          {showRates ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
        </button>
        {showRates && (
          <div className="px-5 pb-5 space-y-4">
            {LRA_RATES.map(cat => (
              <div key={cat.category}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--accent)' }}>{cat.category}</p>
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
                  {cat.items.map((item, i) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between px-3 py-2 text-xs"
                      style={{
                        borderTop: i > 0 ? '1px solid var(--dark-border)' : 'none',
                        backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                      }}
                    >
                      <span style={{ color: 'var(--dark-text)' }}>{item.label}</span>
                      <span className="font-bold" style={{ color: 'var(--accent)' }}>{item.rate}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Channels */}
      <div
        className="rounded-xl p-5"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <div className="flex items-center gap-2 mb-4">
          <CreditCard size={16} style={{ color: 'var(--accent)' }} />
          <h4 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Payment Channels</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PAYMENT_CHANNELS.map(ch => (
            <div
              key={ch.label}
              className="rounded-lg p-3 flex flex-col gap-1"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}
            >
              <span className="text-lg">{ch.icon}</span>
              <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{ch.label}</p>
              <p className="text-xs" style={{ color: ch.color }}>{ch.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section 51 Penalty Notice */}
      <div
        className="rounded-xl p-4 flex gap-3"
        style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)' }}
      >
        <AlertTriangle size={16} style={{ color: '#f87171', flexShrink: 0, marginTop: 2 }} />
        <div className="text-xs space-y-1">
          <p className="font-bold" style={{ color: '#f87171' }}>Section 51 — Penalty Notice</p>
          <p style={{ color: 'var(--dark-muted)' }}>
            Late payment incurs a <span style={{ color: '#fca5a5' }}>10% initial penalty</span> plus{' '}
            <span style={{ color: '#fca5a5' }}>5% per month</span> thereafter. Under-declaration of market value 
            is subject to reassessment and additional penalties. TIN is required for all filings. 
            Due date: <span style={{ color: '#fca5a5' }}>July 1st annually</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
